import {
  usePositioner,
  useResizeObserver,
  useMasonry,
  useScrollToIndex,
} from 'masonic';
import React, {useCallback, useEffect, useRef} from 'react';
import ElementInView from '@/components/element-in-view';
import InfiniteLoader from '@/components/infinite-loader';
import styles from './VirtualizedMasonryGrid.module.scss';
import computeColumns from './hooks/computeColumns';
import useContainerScroller from './hooks/useContainerScroller';
import useContainerSize from './hooks/useContainerSize';

export type VirtualizedMasonryGridProps<T> = {
  testID?: string;
  items: T[];
  renderItem: (props: {data: T; index: number}) => React.ReactElement;
  minColumnWidth?: number;
  maxColumnWidth?: number;
  columnGutter?: number;
  overscanBy?: number;
  itemHeightEstimate?: number;
  onEndReached?: () => void;
  isLoadingMore?: boolean;
  scrollRef?: React.RefObject<HTMLElement>;
  /**
   * When provided, the grid saves the first visible item index on scroll and
   * restores it via useScrollToIndex on remount (e.g. back-navigation).
   */
  scrollRestorationKey?: string;
  /**
   * Additional dependency for the masonic positioner. When this value changes,
   * the position cache is cleared and the grid re-layouts from scratch.
   * Safeguard against masonic crashing when items shrink unexpectedly.
   */
  positionerDep?: unknown;
};

// Module-level cache so scroll position survives React remounts within a session.
const scrollIndexCache = new Map<string, number>();

const DEFAULT_MIN_COLUMN_WIDTH = 280;
const DEFAULT_MAX_COLUMN_WIDTH = 320;
const DEFAULT_COLUMN_GUTTER = 16;
const DEFAULT_OVERSCAN_BY = 2;
const DEFAULT_ITEM_HEIGHT_ESTIMATE = 400;
const ELEMENT_IN_VIEW_HEIGHT = 10;

function VirtualizedMasonryGrid<T>({
  testID = 'virtualized-masonry-grid',
  items,
  renderItem,
  minColumnWidth = DEFAULT_MIN_COLUMN_WIDTH,
  maxColumnWidth = DEFAULT_MAX_COLUMN_WIDTH,
  columnGutter = DEFAULT_COLUMN_GUTTER,
  overscanBy = DEFAULT_OVERSCAN_BY,
  itemHeightEstimate = DEFAULT_ITEM_HEIGHT_ESTIMATE,
  onEndReached,
  isLoadingMore = false,
  scrollRef,
  scrollRestorationKey,
  positionerDep,
}: VirtualizedMasonryGridProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const resolvedScrollRef = scrollRef ?? containerRef;
  const {width: containerWidth} = useContainerSize(containerRef);
  const {height} = useContainerSize(resolvedScrollRef);
  const {scrollTop, isScrolling} = useContainerScroller(resolvedScrollRef);

  const {columnCount, columnWidth} = computeColumns(
    containerWidth,
    minColumnWidth,
    maxColumnWidth,
    columnGutter
  );

  // Pass columnCount explicitly so masonic skips its own column calculation.
  // Using [columnCount] as deps means the positioner (and item layout) only
  // resets when the number of columns actually changes, not on every pixel of
  // container width fluctuation (e.g. scrollbar appearing on back-navigation).
  const positioner = usePositioner(
    {width: containerWidth, columnCount, columnWidth, columnGutter},
    [columnCount, positionerDep]
  );

  const resizeObserver = useResizeObserver(positioner);

  const scrollToIndex = useScrollToIndex(positioner, {
    element: resolvedScrollRef,
    align: 'top',
    height,
  });

  // Restore scroll position on mount using the saved item index.
  useEffect(() => {
    if (!scrollRestorationKey) return;
    const savedIndex = scrollIndexCache.get(scrollRestorationKey);
    if (savedIndex != null && savedIndex > 0) {
      requestAnimationFrame(() => {
        scrollToIndex(savedIndex);
      });
    }
    // Run only on mount — scrollToIndex is a stable ref from masonic.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRender = useCallback(
    (startIndex: number, endIndex: number) => {
      if (scrollRestorationKey) {
        scrollIndexCache.set(
          scrollRestorationKey,
          Math.floor((endIndex - startIndex) / 2 + startIndex)
        );
      }
    },
    [scrollRestorationKey]
  );

  const MasonryItem = useCallback(
    ({data, index}: {data: T; index: number; width: number}) =>
      renderItem({data, index}),
    [renderItem]
  );

  const masonryElement = useMasonry({
    positioner,
    resizeObserver,
    scrollTop,
    isScrolling,
    height,
    containerRef,
    items,
    overscanBy,
    itemHeightEstimate,
    render: MasonryItem,
    onRender: handleRender,
  });

  return (
    <div data-testid={testID} className={styles.container}>
      <div ref={containerRef}>{masonryElement}</div>
      {onEndReached ? (
        <ElementInView
          key={items.length}
          height={ELEMENT_IN_VIEW_HEIGHT}
          onInView={onEndReached}
        />
      ) : null}
      <InfiniteLoader
        isLoading={isLoadingMore}
        containerClassName={styles.loaderOverlay}
      />
    </div>
  );
}

export default VirtualizedMasonryGrid;
